import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { ADMIN_SESSION_EVENT, getSelectedAdminSession, sessionParam } from "../utils/adminSession";
import { buildMarksSummary, buildModuleDetailsRows, fetchAmciDesign, fetchGradePoints, printAmciDocument, resolveGradePointByPercentage, resolveStudentPhoto } from "../utils/amciDesign";

const API = "/backend/public/api";
const TRAINING_THUMBNAIL_ROOT = "/uploads/training-thumbnails";

function Card({ children }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid rgba(148, 163, 184, 0.16)",
        borderRadius: "22px",
        boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
        padding: "20px",
      }}
    >
      {children}
    </div>
  );
}

export function CertifiedStudentsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/student-applications`, {
        params: { workflow_stage: "certified", status: "approved", session_year: sessionParam(), _ts: Date.now() },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
    const reload = () => loadRows();
    window.addEventListener(ADMIN_SESSION_EVENT, reload);
    return () => window.removeEventListener(ADMIN_SESSION_EVENT, reload);
    }, [loadRows]);

    const printCertificate = async (row) => {
      const design = await fetchAmciDesign("certificate");
      const marksRes = await axios.get(`${API}/student-applications/${row.id}/marks`).catch(() => ({ data: { rows: [] } }));
      const marksRows = Array.isArray(marksRes.data?.rows) ? marksRes.data.rows : [];
      const summary = buildMarksSummary(marksRows);
      const gradeRows = await fetchGradePoints();
      const gradeInfo = resolveGradePointByPercentage(summary.percentage, gradeRows);
      printAmciDocument("certificate", design, {
        student_name: row.student_name,
        father_name: row.father_name,
        registration_no: row.registration_no,
        dob: row.dob,
        course_name: row.course_name,
        admission_date: row.admission_date,
        course_completion_date: row.course_completion_date,
        study_centre: row.study_centre_name || "Franchise Centre",
        centre_name: row.study_centre_name || "Franchise Centre",
        centre_code: row.study_centre_login || row.centre_code || "",
        centre_address: row.study_centre_address || row.centre_address || row.franchise_address || "",
        session_year: row.session_year,
        certificate_no: row.application_no || row.registration_no,
        issue_date: row.updated_at || "",
        grade: gradeInfo.grade,
        grade_point: gradeInfo.grade_point,
        percentage: `${summary.percentage}%`,
        total_obtained: summary.totalObtained,
        total_full_marks: summary.totalFull,
        verification_qr: row.registration_no || "Verification QR",
        student_photo: resolveStudentPhoto(row),
      });
    };

    const printMarksheet = async (row) => {
      const res = await axios.get(`${API}/student-applications/${row.id}/marks`);
      const marksRows = Array.isArray(res.data?.rows) ? res.data.rows : [];
      const summary = buildMarksSummary(marksRows);
      const design = await fetchAmciDesign("marksheet");
      const gradeRows = await fetchGradePoints();
      const gradeInfo = resolveGradePointByPercentage(summary.percentage, gradeRows);
      printAmciDocument("marksheet", design, {
        student_name: row.student_name,
        registration_no: row.registration_no,
        dob: row.dob,
        course_name: row.course_name,
        admission_date: row.admission_date,
        course_completion_date: row.course_completion_date,
        study_centre: row.study_centre_name || "Franchise Centre",
        centre_name: row.study_centre_name || "Franchise Centre",
        centre_code: row.study_centre_login || row.centre_code || "",
        module_details: buildModuleDetailsRows(marksRows),
        total_obtained: summary.totalObtained,
        total_full_marks: summary.totalFull,
        total_pass_marks: summary.totalPass,
        obtained_marks: summary.totalObtained,
        theory_obtained_marks: summary.theoryObtained,
        theory_full_marks: summary.theoryFull,
        theory_pass_marks: summary.theoryPass,
        practical_obtained_marks: summary.practicalObtained,
        practical_full_marks: summary.practicalFull,
        practical_pass_marks: summary.practicalPass,
        project_assignment_obtained_marks: summary.projectObtained,
        project_assignment_full_marks: summary.projectFull,
        project_assignment_pass_marks: summary.projectPass,
        viva_obtained_marks: summary.vivaObtained,
        viva_full_marks: summary.vivaFull,
        viva_pass_marks: summary.vivaPass,
        percentage: `${summary.percentage}%`,
        grade: gradeInfo.grade,
        issue_date: row.updated_at || "",
        student_photo: resolveStudentPhoto(row),
      });
    };

  return (
    <DashboardLayout>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: 0, color: "#0f172a" }}>Certified Student</h3>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>Session: {getSelectedAdminSession()} | Only certified students are shown in this list.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={loadRows}>
            Refresh
          </button>
        </div>
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>Registration No</th>
                <th>Student</th>
                  <th>Course</th>
                  <th>Session</th>
                  <th>Study Centre</th>
                  <th>Action</th>
                </tr>
              </thead>
            <tbody>
              {!loading && rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.registration_no || "-"}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <img
                        src={row.photo ? `/uploads/${row.photo}` : "https://placehold.co/56x56/e2e8f0/475569?text=ST"}
                        alt={row.student_name || "Student"}
                        style={{ width: "56px", height: "56px", borderRadius: "16px", objectFit: "cover", border: "1px solid #e2e8f0" }}
                      />
                      <div>
                        <div className="fw-bold">{row.student_name || "-"}</div>
                        <div className="small text-muted">{row.application_no || "-"}</div>
                      </div>
                    </div>
                  </td>
                    <td>{row.course_name || "-"}</td>
                    <td>{row.session_year || "-"}</td>
                    <td>{row.study_centre_name || "-"}</td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => printMarksheet(row)}>
                          Marksheet Download
                        </button>
                        <button type="button" className="btn btn-sm btn-success" onClick={() => printCertificate(row)}>
                          Certificate Download
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">No certified student found.</td>
                  </tr>
                ) : null}
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">Loading certified students...</td>
                  </tr>
                ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </DashboardLayout>
  );
}

export function FranchiseTrainingAdmin() {
  const initialForm = { title: "", description: "", academy_category: "", video_url: "", thumbnail: null, is_active: true };
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));
  const [previewVideo, setPreviewVideo] = useState(null);

  const loadRows = useCallback(async () => {
    const res = await axios.get(`${API}/admin/franchise-training-videos`, { params: { _ts: Date.now() } });
    setRows(Array.isArray(res.data) ? res.data : []);
  }, []);

  useEffect(() => {
    loadRows().catch(() => setRows([]));
  }, [loadRows]);

  useEffect(() => {
    axios.get(`${API}/academy-categories`).then((res) => {
      setCategories(Array.isArray(res.data) ? res.data : []);
    }).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSubmit = async () => {
    if (!form.title || !form.video_url) return;
    try {
      setSaving(true);
      const payload = new FormData();
      payload.append("title", form.title);
      payload.append("description", form.description || "");
      payload.append("academy_category", form.academy_category || "");
      payload.append("video_url", form.video_url);
      payload.append("is_active", form.is_active ? "1" : "0");
      if (form.thumbnail instanceof File) {
        payload.append("thumbnail", form.thumbnail);
      }
      if (editingId) {
        await axios.post(`${API}/admin/franchise-training-videos/${editingId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(`${API}/admin/franchise-training-videos`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setForm(initialForm);
      setEditingId(null);
      await loadRows();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/admin/franchise-training-videos/${id}`);
    await loadRows();
  };

  const gridTemplateColumns = viewportWidth <= 768 ? "1fr" : viewportWidth <= 1100 ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))";
  const embedUrl = (url) => {
    const value = String(url || "").trim();
    const embedMatch = /youtube(?:-nocookie)?\.com\/embed\/([^?&/]+)/i.exec(value);
    const watchMatch = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/i.exec(value);
    const videoId = embedMatch?.[1] || watchMatch?.[1];
    if (videoId) {
      return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&controls=1&modestbranding=1&rel=0&iv_load_policy=3&fs=0&playsinline=1&cc_load_policy=0&showinfo=0&disablekb=0`;
    }
    return value;
  };

  return (
    <DashboardLayout>
      <div style={{ display: "grid", gap: "18px" }}>
        <Card>
          <h3 style={{ marginTop: 0 }}>Franchise Training Control</h3>
          <p style={{ color: "#64748b" }}>Videos saved here will be shown only in the franchise panel training section.</p>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Title</label>
              <input className="form-control" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} />
            </div>
            <div className="col-md-6">
              <label className="form-label">Assign Category</label>
              <select className="form-select" value={form.academy_category} onChange={(e) => setForm((prev) => ({ ...prev, academy_category: e.target.value }))}>
                <option value="">All Categories</option>
                {categories.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Video URL</label>
              <input className="form-control" value={form.video_url} onChange={(e) => setForm((prev) => ({ ...prev, video_url: e.target.value }))} />
              <div className="form-text">YouTube watch link, short link, বা embed link use করতে পারবেন.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label">Thumbnail</label>
              <input className="form-control" type="file" accept=".jpg,.jpeg,.jfif,.png,.webp" onChange={(e) => setForm((prev) => ({ ...prev, thumbnail: e.target.files?.[0] || null }))} />
            </div>
            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea className="form-control" rows="3" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}></textarea>
            </div>
            <div className="col-12">
              <div className="form-check">
                <input id="training-active" className="form-check-input" type="checkbox" checked={form.is_active} onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))} />
                <label className="form-check-label" htmlFor="training-active">Active for franchise panel</label>
              </div>
            </div>
          </div>
          <div style={{ marginTop: "16px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving..." : editingId ? "Update Video" : "Save Video"}
            </button>
            {editingId ? (
              <button type="button" className="btn btn-outline-secondary" onClick={() => { setEditingId(null); setForm(initialForm); }}>
                Cancel Edit
              </button>
            ) : null}
          </div>
        </Card>

        <Card>
          <h4 style={{ marginTop: 0 }}>Uploaded Training Videos</h4>
          <div style={{ display: "grid", gridTemplateColumns, gap: "18px" }}>
            {rows.map((row) => (
              <div key={row.id} style={{ border: "1px solid rgba(148, 163, 184, 0.16)", borderRadius: "20px", overflow: "hidden", background: "linear-gradient(180deg, #ffffff 0%, #fcfdff 100%)" }}>
                <button
                  type="button"
                  onClick={() => setPreviewVideo(row)}
                  style={{ border: "none", padding: 0, width: "100%", background: "transparent", cursor: "pointer" }}
                >
                  <img
                    src={row.thumbnail ? `${TRAINING_THUMBNAIL_ROOT}/${row.thumbnail}` : "https://placehold.co/640x360/e2e8f0/475569?text=Training"}
                    alt={row.title || "Training thumbnail"}
                    style={{ width: "100%", height: "180px", objectFit: "cover", borderBottom: "1px solid rgba(148, 163, 184, 0.16)" }}
                  />
                </button>
                <div style={{ padding: "16px" }}>
                  <div className="fw-bold" style={{ color: "#0f172a" }}>{row.title}</div>
                  <div className="small text-muted" style={{ marginTop: "4px", minHeight: "36px" }}>{row.description || "-"}</div>
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginTop: "10px", fontSize: "13px" }}>
                    <span className="badge text-bg-light">{row.academy_category || "All Categories"}</span>
                    <span className={`badge ${row.is_active ? "text-bg-success" : "text-bg-secondary"}`}>{row.is_active ? "Active" : "Inactive"}</span>
                  </div>
                  <div style={{ marginTop: "12px", fontSize: "12px", color: "#64748b", wordBreak: "break-all" }}>{row.video_url}</div>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setEditingId(row.id);
                        setForm({
                          title: row.title || "",
                          description: row.description || "",
                          academy_category: row.academy_category || "",
                          video_url: row.video_url || "",
                          thumbnail: null,
                          is_active: !!row.is_active,
                        });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${row.is_active ? "btn-outline-warning" : "btn-outline-success"}`}
                      onClick={async () => {
                        const payload = new FormData();
                        payload.append("title", row.title || "");
                        payload.append("description", row.description || "");
                        payload.append("academy_category", row.academy_category || "");
                        payload.append("video_url", row.video_url || "");
                        payload.append("is_active", row.is_active ? "0" : "1");
                        await axios.post(`${API}/admin/franchise-training-videos/${row.id}`, payload, { headers: { "Content-Type": "multipart/form-data" } });
                        await loadRows();
                      }}
                    >
                      {row.is_active ? "Inactive" : "Active"}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {rows.length === 0 ? <div className="text-muted">No training video uploaded yet.</div> : null}
          </div>
        </Card>

        {previewVideo ? (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: "18px", zIndex: 4000 }}
            onClick={() => setPreviewVideo(null)}
          >
            <div
              style={{ width: "min(960px, 100%)", background: "#ffffff", borderRadius: "24px", padding: "18px", boxShadow: "0 28px 70px rgba(15,23,42,0.26)" }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: "14px", alignItems: "center", marginBottom: "14px", flexWrap: "wrap" }}>
                <div>
                  <h3 style={{ margin: 0, color: "#0f172a" }}>{previewVideo.title}</h3>
                </div>
                <button type="button" className="btn btn-danger" onClick={() => setPreviewVideo(null)}>
                  Close
                </button>
              </div>
              <div style={{ width: "100%", aspectRatio: "16 / 9", borderRadius: "18px", overflow: "hidden", border: "1px solid rgba(148,163,184,.18)", background: "#000" }}>
                <iframe
                  title={previewVideo.title}
                  src={embedUrl(previewVideo.video_url)}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
}

export function TicketSystemAdmin() {
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [replyForm, setReplyForm] = useState({ status: "in_progress", admin_reply: "" });

  const loadRows = useCallback(async () => {
    const res = await axios.get(`${API}/franchise-tickets`, { params: { _ts: Date.now() } });
    setRows(Array.isArray(res.data) ? res.data : []);
  }, []);

  useEffect(() => {
    loadRows().catch(() => setRows([]));
  }, [loadRows]);

  const saveReply = async (id) => {
    await axios.post(`${API}/franchise-tickets/${id}/reply`, replyForm);
    setEditingId(null);
    setReplyForm({ status: "in_progress", admin_reply: "" });
    await loadRows();
  };

  return (
    <DashboardLayout>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div>
            <h3 style={{ margin: 0 }}>Ticket System</h3>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>Franchise and admin communication tickets are managed here.</p>
          </div>
          <button type="button" className="btn btn-outline-secondary" onClick={loadRows}>Refresh</button>
        </div>
        <div className="table-responsive">
          <table className="table align-middle" style={{ border: "1px solid rgba(148, 163, 184, 0.18)", borderRadius: "14px", overflow: "hidden" }}>
            <thead>
              <tr>
                <th style={{ background: "#fff7ed", color: "#7c2d3a", textAlign: "center", borderRight: "1px solid rgba(148, 163, 184, 0.18)" }}>Franchise</th>
                <th style={{ background: "#fff7ed", color: "#7c2d3a", textAlign: "center", borderRight: "1px solid rgba(148, 163, 184, 0.18)" }}>Subject</th>
                <th style={{ background: "#fff7ed", color: "#7c2d3a", textAlign: "center", borderRight: "1px solid rgba(148, 163, 184, 0.18)" }}>Message</th>
                <th style={{ background: "#fff7ed", color: "#7c2d3a", textAlign: "center", borderRight: "1px solid rgba(148, 163, 184, 0.18)" }}>Status</th>
                <th style={{ background: "#fff7ed", color: "#7c2d3a", textAlign: "center" }}>Reply</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td style={{ borderRight: "1px solid rgba(148, 163, 184, 0.16)", textAlign: "center", verticalAlign: "middle", background: "rgba(248,250,252,0.72)" }}>
                    <div className="fw-bold">{row.centre_name || row.login_id || "-"}</div>
                    <div className="small text-muted">{row.centre_code || "-"}</div>
                  </td>
                  <td style={{ borderRight: "1px solid rgba(148, 163, 184, 0.16)", fontWeight: 700, color: "#0f172a", textAlign: "center", verticalAlign: "middle" }}>{row.subject}</td>
                  <td style={{ borderRight: "1px solid rgba(148, 163, 184, 0.16)", color: "#334155", textAlign: "center", verticalAlign: "middle" }}>{row.message}</td>
                  <td className="text-capitalize text-center" style={{ borderRight: "1px solid rgba(148, 163, 184, 0.16)", verticalAlign: "middle" }}>
                    <span style={{
                      display: "inline-flex",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      fontWeight: 800,
                      background: row.status === "closed" ? "#dcfce7" : row.status === "in_progress" ? "#dbeafe" : "#fef3c7",
                      color: row.status === "closed" ? "#166534" : row.status === "in_progress" ? "#1d4ed8" : "#92400e",
                    }}>
                      {String(row.status || "open").replaceAll("_", " ")}
                    </span>
                  </td>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                    {editingId === row.id ? (
                      <div style={{ display: "grid", gap: "8px", minWidth: "240px" }}>
                        <select className="form-select" value={replyForm.status} onChange={(e) => setReplyForm((prev) => ({ ...prev, status: e.target.value }))}>
                          <option value="open">Open</option>
                          <option value="in_progress">In Progress</option>
                          <option value="closed">Closed</option>
                        </select>
                        <textarea className="form-control" rows="3" value={replyForm.admin_reply} onChange={(e) => setReplyForm((prev) => ({ ...prev, admin_reply: e.target.value }))}></textarea>
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          <button type="button" className="btn btn-sm btn-primary" onClick={() => saveReply(row.id)}>Save</button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: "8px" }}>
                        <div>{row.admin_reply || "-"}</div>
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => { setEditingId(row.id); setReplyForm({ status: row.status || "in_progress", admin_reply: row.admin_reply || "" }); }}>
                          Reply / Update
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">No ticket found.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </DashboardLayout>
  );
}



